import { type RouteRecordRaw } from 'vue-router'
import { PERMISSION_READ_ASSETS, PERMISSION_READ_PORTFOLIOS } from '@/vue/helpers/permissions'
import { check } from '../guards/permission'
import type { Empty, PortfolioWithAssetId, PortfolioWithAssetIdAndYear, PortfolioWithYear, WithPortfolioId } from './types'

type PortfolioWithMeasureId = WithPortfolioId & { measure_id: number }
type PortfolioWithAcquisitionId = WithPortfolioId & { acquisition_id: number }

export interface RouteParams {
    'portfolio.overview': PortfolioWithYear
    'portfolio.input-overview': PortfolioWithYear
    'portfolio.map': PortfolioWithYear
    'portfolio.esg-data': PortfolioWithYear
    'portfolio.roadmap': PortfolioWithYear
    'portfolio.roadmap-data-collection': PortfolioWithYear
    'portfolio.measure': PortfolioWithMeasureId
    'portfolio.measure-data-collection': WithPortfolioId & { measure_id?: string }
    'portfolio.acquisition': PortfolioWithAcquisitionId
    'portfolio.acquisition-data-collection': PortfolioWithAcquisitionId
    'portfolio.governance': WithPortfolioId
    'portfolio.data-request': WithPortfolioId
    'portfolio.certifications': PortfolioWithYear
    'portfolio.reports-overview': PortfolioWithYear
    'portfolio.custom-roadmap-targets-and-benchmarks': PortfolioWithYear
    'portfolio.settings': WithPortfolioId
    'portfolio.emission-factors': WithPortfolioId
    'portfolio.regulatory': PortfolioWithYear
    'portfolio.gresb-data': PortfolioWithYear
    'portfolio.table': PortfolioWithYear
    'portfolio.saved-reports': PortfolioWithYear
    'portfolio.dashboards.list': PortfolioWithYear
    'portfolio.dashboards.edit': { dashboard_id: string }
    'portfolio.dashboards.manage': Empty
    'portfolio.financial': PortfolioWithYear
    'portfolio.performance': PortfolioWithYear
    'portfolio.performance.edit.emissions': PortfolioWithYear
    'portfolio.physical-climate': PortfolioWithYear
    'portfolio.physical-climate-measures': WithPortfolioId
    'portfolio.physical-climate-measure': WithPortfolioId & { measure_id: string }
    'portfolio.metrics': PortfolioWithYear
    'portfolio.audit-log': PortfolioWithYear
    'portfolio.input-portal.audit-log': PortfolioWithYear

    // Portfolio-level asset routes
    'portfolio-asset.list': PortfolioWithYear
    'portfolio-asset.meter-completion-list': PortfolioWithYear
    'portfolio-asset.collection.list': PortfolioWithYear
    'portfolio.alerts': PortfolioWithYear
    'portfolio-asset.asset-details-list': PortfolioWithYear
    'portfolio-asset.automations': WithPortfolioId
    'portfolio-asset.meters-overview': PortfolioWithAssetIdAndYear
    'portfolio-asset.consumption-coverage': PortfolioWithYear
    'portfolio-asset.bill-scraping-log': WithPortfolioId
    'portfolio-asset.asset-mappings-list': WithPortfolioId
    'portfolio-asset.meter-mappings-list': PortfolioWithAssetId
    'portfolio.annual-report': PortfolioWithYear
}

/**
 * Portfolio routes for analytics mode
 */
export const portfolioAnalyticsRoutes: RouteRecordRaw[] = [
    {
        path: 'portfolio/:portfolio_id/:reporting_year',
        component: () => import('@/vue/pages/analytics/portfolio/Layout.vue'),
        beforeEnter: check([PERMISSION_READ_PORTFOLIOS], 'dashboard'),
        children: [
            {
                path: '',
                redirect: { name: 'portfolio.overview' },
            },
            {
                path: 'overview',
                name: 'portfolio.overview',
                component: () => import('@/vue/pages/portfolio-overview/PortfolioOverview.vue'),
                meta: {
                    redirectsTo: 'portfolio.input-overview',
                    fluid: true,
                },
            },
            {
                path: 'map',
                name: 'portfolio.map',
                component: () => import('@/vue/pages/portfolio/map/MapPage.vue'),
                meta: {
                    redirectsTo: 'portfolio.input-overview',
                    fluid: true,
                },
            },
            {
                path: 'esg-data',
                name: 'portfolio.esg-data',
                component: () => import('@/vue/pages/esg/portfolio/EsgDataPage.vue'),
                meta: {
                    redirectsTo: 'portfolio.input-overview',
                    title: 'general.sub-navigation.performance',
                    companyWideRoute: 'company.analytics',
                },
                beforeEnter: check([PERMISSION_READ_PORTFOLIOS, PERMISSION_READ_ASSETS], 'portfolio.overview', 'portfolio-esg-data-page'),
            },
            {
                path: 'financial',
                name: 'portfolio.financial',
                component: () => import('@/vue/pages/portfolio/financial/PortfolioFinancialPage.vue'),
                meta: {
                    redirectsTo: 'portfolio.input-overview',
                    title: 'general.sub-navigation.financial',
                },
                beforeEnter: check([PERMISSION_READ_PORTFOLIOS, PERMISSION_READ_ASSETS], 'portfolio.overview', 'portfolio-financial-dashboard'),
            },
            {
                path: 'roadmap',
                name: 'portfolio.roadmap',
                component: () => import('@/vue/pages/roadmap/portfolio/PortfolioRoadmapPage.vue'),
                meta: {
                    title: 'general.sub-navigation.roadmap-analysis',
                    fluid: true,
                    redirectsTo: 'portfolio.roadmap-data-collection',
                },
                beforeEnter: check([PERMISSION_READ_PORTFOLIOS], 'portfolio.overview', 'portfolio-roadmap-page'),
            },
            {
                path: 'measure/:measure_id?',
                name: 'portfolio.measure',
                component: () => import('@/vue/pages/roadmap/measure/MeasurePage.vue'),
                meta: {
                    title: 'portfolio.roadmap.assets.measures',
                    redirectsTo: 'portfolio.measure-data-collection',
                },
            },
            {
                path: 'acquisition/:acquisition_id',
                name: 'portfolio.acquisition',
                component: () => import('@/vue/pages/roadmap/acquisition/AcquisitionPage.vue'),
                meta: {
                    title: 'portfolio.roadmap.assets.acquisitions',
                    redirectsTo: 'portfolio.acquisition-data-collection',
                },
            },
            {
                path: 'regulatory',
                name: 'portfolio.regulatory',
                component: () => import('@/vue/pages/portfolio/regulatory/RegulatoryPage.vue'),
                meta: {
                    redirectsTo: 'portfolio.input-overview',
                    title: 'general.regulatory',
                },
                beforeEnter: check([PERMISSION_READ_PORTFOLIOS], 'portfolio.overview', 'new-portfolio-regulatory-page'),
            },
            {
                path: 'gresb-data',
                name: 'portfolio.gresb-data',
                component: () => import('@/vue/pages/gresb/GRESBPage.vue'),
                meta: {
                    redirectsTo: 'portfolio.input-overview',
                    title: 'general.tabs.scores',
                },
                beforeEnter: check([PERMISSION_READ_PORTFOLIOS], 'portfolio.overview', 'portfolio-scores-page'),
            },
            {
                path: 'metrics',
                name: 'portfolio.metrics',
                component: () => import('@/vue/pages/metrics/PortfolioMetricsPage.vue'),
                meta: {
                    title: 'general.metrics',
                    fluid: true,
                    redirectsTo: 'portfolio.input-overview',
                },
            },
            {
                path: 'physical-climate',
                name: 'portfolio.physical-climate',
                component: () => import('@/vue/pages/portfolio/physical-climate/PortfolioPhysicalClimatePage.vue'),
                meta: {
                    title: 'asset.input-portal.physical-climate',
                    fluid: false,
                    redirectsTo: canAccessFeature => canAccessFeature('physical-climate-measures') ? 'portfolio.physical-climate-measures' : 'portfolio.input-overview',
                },
            },
            {
                path: 'assets',
                name: 'portfolio-asset.list',
                component: () => import('@/vue/pages/asset/AssetListPage.vue'),
                meta: {
                    fluid: true,
                    redirectsTo: 'portfolio-asset.meter-completion-list',
                    title: 'general.overview',
                },
                beforeEnter: check([PERMISSION_READ_ASSETS], 'portfolio.overview'),
            },

            // Custom Dashboards
            {
                path: 'dashboards',
                children: [
                    {
                        path: '',
                        name: 'portfolio.dashboards.list',
                        component: () => import('@/vue/pages/custom/dashboard/PortfolioDashboardsListPage.vue'),
                        meta: {
                            redirectsTo: 'portfolio.input-overview',
                            title: 'general.sub-navigation.custom-dashboards',
                            fluid: true,
                        },
                    },
                    {
                        path: 'manage',
                        name: 'portfolio.dashboards.manage',
                        component: () => import('@/vue/pages/custom/dashboard/PortfolioDashboardsManagePage.vue'),
                        meta: {
                            redirectsTo: 'portfolio.input-overview',
                            title: 'general.sub-navigation.custom-dashboards',
                            fluid: true,
                        },
                    },
                    {
                        path: 'edit/:dashboard_id',
                        name: 'portfolio.dashboards.edit',
                        component: () => import('@/vue/pages/custom/dashboard/PortfolioDashboardEditPage.vue'),
                        meta: {
                            redirectsTo: 'portfolio.input-overview',
                            title: 'general.sub-navigation.custom-dashboards',
                            fluid: true,
                        },
                    },
                ],
            },

            {
                path: 'audit-log',
                name: 'portfolio.audit-log',
                component: () => import('@/vue/pages/portfolio/AuditLogPage.vue'),
                meta: {
                    title: 'audit-log.title',
                    fluid: true,
                    redirectsTo: 'portfolio.input-portal.audit-log',
                    feature: {
                        flag: 'new-audit-system',
                        fallbackRoute: 'portfolio.overview',
                    },
                },
            },
            {
                path: ':catchAll(.*)',
                redirect: 'portfolio.overview',
            },
        ],
    },
]

/**
 * Portfolio routes for collection mode
 */
export const portfolioCollectionRoutes: RouteRecordRaw[] = [
    {
        path: 'portfolio/:portfolio_id/:reporting_year',
        component: () => import('@/vue/pages/collection/portfolio/Layout.vue'),
        beforeEnter: check([PERMISSION_READ_PORTFOLIOS], 'dashboard'),
        children: [
            {
                path: 'overview',
                name: 'portfolio.input-overview',
                component: () => import('@/vue/pages/portfolio/input-portfolio-overview/InputPortfolioOverviewPage.vue'),
                meta: {
                    title: 'general.overview',
                    redirectsTo: 'portfolio.overview',
                },
            },
            {
                path: 'asset-list',
                name: 'portfolio-asset.collection.list',
                component: () => import('@/vue/pages/asset/list/AssetListCollectionPage.vue'),
                meta: {
                    title: 'general.assets',
                    redirectsTo: 'portfolio-asset.list',
                    matchRouteByPrefix: false,
                    fluid: true,
                },
            },
            {
                path: 'alerts',
                name: 'portfolio.alerts',
                component: () => import('@/vue/pages/portfolio/alerts/PortfolioAlertsPage.vue'),
                meta: {
                    title: 'general.alerts-label',
                    redirectsTo: 'portfolio-asset.list',
                    fluid: true,
                },
            },
            {
                path: 'meter-completion-list',
                name: 'portfolio-asset.meter-completion-list',
                component: () => import('@/vue/pages/asset/MeterCompletionAssetListPage.vue'),
                meta: {
                    title: 'general.overview',
                    redirectsTo: 'portfolio-asset.list',
                    matchRouteByPrefix: false,
                    fluid: true,
                },
            },
            {
                path: 'roadmap',
                name: 'portfolio.roadmap-data-collection',
                component: () => import('@/vue/pages/roadmap/portfolio/PortfolioRoadmapPage.vue'),
                meta: {
                    title: 'general.sub-navigation.roadmap-measures',
                    fluid: true,
                    redirectsTo: 'portfolio.roadmap',
                },
                beforeEnter: check([PERMISSION_READ_PORTFOLIOS], 'portfolio.input-overview', 'input-portfolio-roadmap-page'),
            },
            {
                path: 'measure/:measure_id?',
                name: 'portfolio.measure-data-collection',
                component: () => import('@/vue/pages/roadmap/measure/MeasurePage.vue'),
                meta: {
                    title: 'portfolio.roadmap.assets.measures',
                    redirectsTo: 'portfolio.measure',
                    parentRoutes: ['portfolio.roadmap-data-collection'],
                },
            },
            {
                path: 'acquisition/:acquisition_id?',
                name: 'portfolio.acquisition-data-collection',
                component: () => import('@/vue/pages/roadmap/acquisition/AcquisitionPage.vue'),
                meta: {
                    title: 'portfolio.roadmap.assets.acquisitions',
                    redirectsTo: 'portfolio.acquisition',
                },
            },
            {
                path: 'governance',
                name: 'portfolio.governance',
                component: () => import('@/vue/pages/governance/GovernancePage.vue'),
                meta: {
                    redirectsTo: 'portfolio.overview',
                    title: 'general.governance',
                },
            },
            {
                path: 'data-request',
                name: 'portfolio.data-request',
                component: () => import('@/vue/pages/portfolio/data-request/DataRequestPage.vue'),
                meta: {
                    redirectsTo: 'portfolio.overview',
                    title: 'general.data-request',
                },
            },
            {
                path: 'certifications',
                name: 'portfolio.certifications',
                component: () => import('@/vue/pages/portfolio/certifications/PortfolioCertificationsPage.vue'),
                meta: {
                    redirectsTo: 'portfolio-asset.list',
                    title: 'general.certifications',
                    fluid: true,
                },
            },
            {
                path: 'reports',
                name: 'portfolio.reports-overview',
                component: () => import('@/vue/pages/portfolio/ReportsPage.vue'),
                meta: {
                    redirectsTo: 'portfolio.overview',
                    title: 'general.reports',
                },
            },
            {
                path: 'targets',
                name: 'portfolio.custom-roadmap-targets-and-benchmarks',
                component: () => import('@/vue/pages/targets-and-benchmarks/TargetsAndBenchmarks.vue'),
                meta: {
                    redirectsTo: 'portfolio.overview',
                    title: 'general.sub-navigation.targets-and-benchmarks',
                },
            },
            {
                path: 'settings',
                name: 'portfolio.settings',
                component: () => import('@/vue/pages/portfolio/settings/PortfolioSettingsPage.vue'),
                meta: {
                    redirectsTo: 'portfolio.overview',
                    title: 'general.settings',
                },
            },
            {
                path: 'emission-factors',
                name: 'portfolio.emission-factors',
                component: () => import('@/vue/pages/portfolio/emission-factors/PortfolioEmissionFactorsPage.vue'),
                meta: {
                    redirectsTo: 'portfolio.overview',
                    title: 'portfolio.settings.emission-factor',
                },
            },
            {
                path: 'saved-reports/:reporting_year',
                name: 'portfolio.saved-reports',
                component: () => import('@/vue/pages/portfolio/saved-reports/PortfolioSavedReportsPage.vue'),
                meta: {
                    title: 'general.metrics',
                    fluid: false,
                    redirectsTo: 'portfolio.overview',
                },
            },
            {
                path: 'physical-climate-measures',
                name: 'portfolio.physical-climate-measures',
                component: () => import('@/vue/pages/portfolio/physical-climate/PortfolioPhysicalClimateMeasuresPage.vue'),
                meta: {
                    title: 'asset.input-portal.physical-climate',
                    fluid: false,
                    redirectsTo: 'portfolio.physical-climate',
                },
            },
            {
                path: 'physical-climate-measures/:measure_id',
                name: 'portfolio.physical-climate-measure',
                component: () => import('@/vue/pages/portfolio/physical-climate/PhysicalClimateMeasurePage.vue'),
                meta: {
                    title: 'asset.input-portal.physical-climate',
                    fluid: false,
                    redirectsTo: 'portfolio.physical-climate',
                },
            },
            {
                path: 'performance',
                name: 'portfolio.performance',
                component: () => import('@/vue/pages/portfolio/performance/PortfolioPerformancePage.vue'),
                meta: {
                    title: 'general.performance',
                    fluid: false,
                    redirectsTo: 'portfolio.overview',
                },
                beforeEnter: check([], 'portfolio.overview', 'portfolio-performance-page'),
            },
            {
                path: 'performance/edit/emissions',
                name: 'portfolio.performance.edit.emissions',
                component: () => import('@/vue/pages/portfolio/performance/tabs/emissions/edit/TabEmissionsEdit.vue'),
                meta: {
                    redirectsTo: 'portfolio.overview',
                    parentRoutes: ['portfolio.performance'],
                },
                beforeEnter: check([], 'portfolio.overview', 'portfolio-performance-page'),
            },
            {
                path: 'automations',
                name: 'portfolio-asset.automations',
                component: () => import('@/vue/pages/portfolio/AutomationsPage.vue'),
                meta: {
                    redirectsTo: 'portfolio-asset.list',
                    title: 'general.automations',
                },
            },
            {
                path: 'meters-overview/:reporting_year',
                name: 'portfolio-asset.meters-overview',
                component: () => import('@/vue/pages/asset/meters-overview/MetersOverviewPage.vue'),
                meta: {
                    redirectsTo: 'portfolio-asset.list',
                    fluid: true,
                    title: 'general.meters-list',
                },
            },
            {
                path: 'consumption-coverage/:reporting_year',
                name: 'portfolio-asset.consumption-coverage',
                component: () => import('@/vue/pages/portfolio/consumption-coverage/ConsumptionCoveragePage.vue'),
                meta: {
                    redirectsTo: 'portfolio-asset.list',
                    fluid: true,
                    title: 'asset.input-portal.consumption-coverage',
                },
            },
            {
                path: 'asset-details-list/:reporting_year',
                name: 'portfolio-asset.asset-details-list',
                component: () => import('@/vue/pages/asset/AssetDetailsListPage.vue'),
                meta: {
                    fluid: true,
                    redirectsTo: 'portfolio-asset.list',
                    title: 'general.asset-details',
                },
            },
            {
                path: 'bill-scraping-log',
                name: 'portfolio-asset.bill-scraping-log',
                component: () => import('@/vue/pages/portfolio/AutomationsPage.vue'),
                meta: {
                    redirectsTo: 'portfolio-asset.automations',
                    title: 'general.bill-scraping-log',
                },
            },
            {
                path: 'asset-mappings',
                name: 'portfolio-asset.asset-mappings-list',
                component: () => import('@/vue/pages/portfolio/AutomationsPage.vue'),
                meta: {
                    redirectsTo: 'portfolio-asset.automations',
                    fluid: true,
                    title: 'general.asset-mappings',
                },
            },
            {
                path: 'audit-log',
                name: 'portfolio.input-portal.audit-log',
                component: () => import('@/vue/pages/portfolio/AuditLogPage.vue'),
                meta: {
                    title: 'audit-log.title',
                    redirectsTo: 'portfolio.audit-log',
                    feature: {
                        flag: 'new-audit-system',
                        fallbackRoute: 'portfolio.input-overview',
                    },
                },
            },
        ],
    },
]
